/**
 * Reminder & Alert System helpers.
 * Backend contract: GET /workspaces/:id/alerts
 * → { workspace_alerts: Alert[], user_alerts: Alert[] }
 *
 * Alert shape: { id, scope?, type, severity, title, message, due_date?, link?, meta?, created_at? }
 * Real-time: Echo event `.workspace.alert.created` on workspace.{id}.users.{userId}
 */

export const ALERT_SEVERITY = {
  DANGER: 'danger',
  WARNING: 'warning',
  INFO: 'info',
}

export const ALERT_CTA = {
  FIX: 'Fix',
  REVIEW: 'Review',
  APPROVE: 'Approve',
}

export const SEVERITY_PRIORITY = {
  [ALERT_SEVERITY.DANGER]: 0,
  [ALERT_SEVERITY.WARNING]: 1,
  [ALERT_SEVERITY.INFO]: 2,
}

export const SEVERITY_LABELS = {
  [ALERT_SEVERITY.DANGER]: 'Action required',
  [ALERT_SEVERITY.WARNING]: 'Waiting',
  [ALERT_SEVERITY.INFO]: 'Info',
}

/** Default severity by alert/flag type when backend omits it */
export const TYPE_SEVERITY = {
  expired_policies: ALERT_SEVERITY.DANGER,
  policy_overdue: ALERT_SEVERITY.DANGER,
  policy_renewal: ALERT_SEVERITY.WARNING,
  policy_renewal_soon: ALERT_SEVERITY.WARNING,
  pending_approval: ALERT_SEVERITY.WARNING,
  approval_pending: ALERT_SEVERITY.WARNING,
  training_overdue: ALERT_SEVERITY.DANGER,
  training_due: ALERT_SEVERITY.WARNING,
  verification_required: ALERT_SEVERITY.INFO,
  compliance_alert: ALERT_SEVERITY.WARNING,
}

/** Default titles when backend omits them */
export const TYPE_TITLES = {
  expired_policies: 'Expired policies',
  policy_overdue: 'Policy overdue',
  policy_renewal: 'Policy renewal due',
  policy_renewal_soon: 'Policy renewal soon',
  pending_approval: 'Approval pending',
  approval_pending: 'Approval pending',
  training_overdue: 'Training overdue',
  training_due: 'Training due soon',
  verification_required: 'Identity verification required',
  compliance_alert: 'Compliance alert',
}

/**
 * CTA label by type / severity.
 * Approve for approval flows; Fix for danger; Review otherwise.
 */
export function resolveCtaLabel(alert) {
  if (alert?.cta_label) return alert.cta_label

  const type = String(alert?.type ?? '').toLowerCase()
  if (
    type.includes('approval') ||
    type === 'pending_approval' ||
    type === 'approval_pending'
  ) {
    return ALERT_CTA.APPROVE
  }

  const severity = normalizeSeverity(alert?.severity ?? TYPE_SEVERITY[type])
  if (severity === ALERT_SEVERITY.DANGER) return ALERT_CTA.FIX
  return ALERT_CTA.REVIEW
}

export function normalizeSeverity(severity) {
  const value = String(severity ?? '').toLowerCase()
  if (value === 'error' || value === 'critical' || value === 'high') {
    return ALERT_SEVERITY.DANGER
  }
  if (value === 'danger' || value === 'warning' || value === 'info') {
    return value
  }
  if (value === 'medium' || value === 'warn') return ALERT_SEVERITY.WARNING
  if (value === 'low') return ALERT_SEVERITY.INFO
  return ALERT_SEVERITY.INFO
}

/**
 * Build an in-app route for an alert.
 * Prefers server-provided `link`, then type-based fallbacks.
 */
export function buildAlertLink(alert, workspaceId) {
  if (!alert) return null
  if (alert.link) {
    if (typeof alert.link === 'object') return alert.link
    const link = String(alert.link)
    if (link.startsWith('/')) return link
    if (workspaceId) return `/workspaces/${workspaceId}/${link.replace(/^\//, '')}`
    return link
  }

  if (!workspaceId) return null
  const base = `/workspaces/${workspaceId}`
  const type = String(alert.type ?? '').toLowerCase()
  const entityId = alert.meta?.entity_id ?? alert.meta?.id ?? alert.resource_id

  switch (type) {
    case 'expired_policies':
    case 'policy_overdue':
    case 'policy_renewal':
    case 'policy_renewal_soon':
      return entityId ? `${base}/policy-tracker/${entityId}` : `${base}/policy-tracker`
    case 'pending_approval':
    case 'approval_pending':
      return entityId
        ? `${base}/approval-requests/${entityId}`
        : `${base}/approval-requests`
    case 'training_overdue':
    case 'training_due':
    case 'training_assigned':
      return entityId
        ? `${base}/compliance-training/${entityId}`
        : `${base}/compliance-training`
    case 'compliance_alert':
      return entityId
        ? `${base}/compliance-action-center/${entityId}`
        : `${base}/compliance-action-center`
    case 'verification_required':
      return { name: 'profile-settings' }
    default:
      return `${base}/alerts`
  }
}

/**
 * Normalize API / Echo / score-flag payloads into a consistent alert item.
 */
export function normalizeAlert(payload, { workspaceId, source = 'alert' } = {}) {
  if (!payload) return null

  const type = payload.type ?? 'info'
  const id =
    payload.id ??
    payload.key ??
    `${source}-${type}-${payload.created_at ?? Date.now()}`

  const severity = normalizeSeverity(
    payload.severity ?? TYPE_SEVERITY[type] ?? ALERT_SEVERITY.INFO,
  )

  const createdAt = payload.created_at ? new Date(payload.created_at) : null
  const dueDate = payload.due_date ? new Date(payload.due_date) : null

  const alert = {
    id: String(id),
    source,
    scope: payload.scope ?? (source === 'score_flag' ? 'workspace' : 'user'),
    type,
    title: payload.title ?? TYPE_TITLES[type] ?? 'Needs attention',
    message: payload.message ?? payload.body ?? payload.description ?? '',
    severity,
    meta: payload.meta ?? null,
    resource_id: payload.resource_id ?? payload.meta?.entity_id ?? null,
    created_at: createdAt ? createdAt.toISOString() : null,
    created_at_ts: createdAt ? createdAt.getTime() : 0,
    due_date: dueDate ? dueDate.toISOString() : null,
    due_date_ts: dueDate ? dueDate.getTime() : Number.POSITIVE_INFINITY,
  }

  alert.link = buildAlertLink({ ...payload, ...alert }, workspaceId)
  alert.cta_label = resolveCtaLabel(alert)

  return alert
}

/** Sort: danger → warning → info, then soonest due date, then newest */
export function sortAlertsByPriority(alerts) {
  return [...alerts].sort((a, b) => {
    const severityDiff =
      (SEVERITY_PRIORITY[a.severity] ?? 99) - (SEVERITY_PRIORITY[b.severity] ?? 99)
    if (severityDiff !== 0) return severityDiff

    const dueDiff = (a.due_date_ts ?? Infinity) - (b.due_date_ts ?? Infinity)
    if (dueDiff !== 0) return dueDiff

    return (b.created_at_ts ?? 0) - (a.created_at_ts ?? 0)
  })
}

export function dedupeAlerts(alerts) {
  const byId = new Map()
  for (const alert of alerts) {
    if (!alert?.id) continue
    byId.set(String(alert.id), alert)
  }
  return Array.from(byId.values())
}

/** Pending approval items for the dashboard "Items to Approve" row. */
export function isApprovalAlert(alert) {
  return String(alert?.type ?? '').toLowerCase().includes('approval')
}

/**
 * Open-attention alerts for the dashboard summary: expired policies,
 * overdue vendors/trainings, and similar overdue/expired items.
 * Pending approvals are counted separately under "Items to Approve".
 */
export function isOpenAlert(alert) {
  if (isApprovalAlert(alert)) return false
  const haystack = `${alert?.type ?? ''} ${alert?.title ?? ''}`.toLowerCase()
  return /expired|overdue|vendor|training/.test(haystack)
}
